<template>
    <form class="fert-cycle-overview" action="" @submit.stop.prevent="">
        <div class="title">
            <span>{{ $translate('cycle_overview') }}</span>
        </div>

        <div class="cycle-info">
            <div class="total input-container">
                <label for="total">{{ $translate('number_of_thawed_eggs') }}</label>
                <input
                    id="total"
                    v-model="total"
                    name="total"
                    type="number"
                    placeholder="-"
                    min="0"
                    max="50"
                />
                <label for="total" class="icon"><i class="fa fa-pencil"></i></label>
            </div>

            <div class="fields">
                <div class="insemination-type ivf">
                    <div class="input-container">
                        <label for="inseminated"
                            >{{ $translate('eggs_inseminatred') }} ({{ $translate('ivf') }})</label
                        >
                        <input
                            id="inseminated"
                            v-model="inseminated"
                            name="inseminated"
                            type="number"
                            placeholder="-"
                            min="0"
                            max="50"
                        />
                        <label for="inseminated" class="icon"><i class="fa fa-pencil"></i></label>
                    </div>

                    <div class="input-container">
                        <label for="inseminatedFert">
                            {{ $translate('fertilized') }} {{ $translate('ivf') }} (2PN)
                        </label>
                        <input
                            id="inseminatedFert"
                            v-model="inseminatedFert"
                            name="inseminatedFert"
                            type="number"
                            placeholder="-"
                            min="0"
                            max="50"
                        />
                        <label for="inseminatedFert" class="icon">
                            <i class="fa fa-pencil"></i>
                        </label>
                    </div>
                </div>

                <div class="insemination-type icsi">
                    <div class="input-container">
                        <label for="injected">{{ $translate('eggs_injected') }}</label>
                        <input
                            id="injected"
                            v-model="injected"
                            name="injected"
                            type="number"
                            placeholder="-"
                            min="0"
                            max="50"
                        />
                        <label for="injected" class="icon"><i class="fa fa-pencil"></i></label>
                    </div>

                    <div class="input-container">
                        <label for="injectedFert">
                            {{ $translate('fertilized_injected') }} (2PN)</label
                        >
                        <input
                            id="injectedFert"
                            v-model="injectedFert"
                            name="injectedFert"
                            type="number"
                            placeholder="-"
                            min="0"
                            max="50"
                        />
                        <label for="injectedFert" class="icon"><i class="fa fa-pencil"></i></label>
                    </div>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import { mapGetters } from 'vuex'
import Utils from '../../../../business/utils/Utils'

export default {
    name: 'FertCycleOverview',
    props: {
        wells: {
            type: Array,
            required: true
        }
    },
    data: () => ({
        total: '',
        inseminated: '',
        inseminatedFert: '',
        injected: '',
        injectedFert: ''
    }),
    computed: {
        ...mapGetters({
            isFdaFriendly: 'common/isFdaFriendly',
            getPnCount: 'timelapse/getPnCount'
        }),
        defaultInjected() {
            return this.wells.filter(well => {
                const firstFrameMs = well.videoFramesTime[0]
                const firstFrameHours = Utils.convertMsToHM(firstFrameMs)

                return firstFrameHours < 5
            })
        },
        defaultInjectedFert() {
            if (this.isFdaFriendly) return ''

            return this.defaultInjected.filter(well => this.getPnCount(well) === 2)
        }
    },
    mounted() {
        this.setDefaultValues()
    },
    methods: {
        setDefaultValues() {
            this.total = this.wells.length

            if (!this.isFdaFriendly) {
                this.injected = this.defaultInjected.length
                this.injectedFert = this.defaultInjectedFert.length
            }
        }
    }
}
</script>

<style lang="scss" scoped>
$active: $light-3;
$passive: $light;

.fert-cycle-overview {
    margin: 20px 0;

    .title {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 10px;
    }

    .cycle-info {
        display: grid;
        grid-template-columns: minmax(auto, min-content) 1fr;
        column-gap: 10px;

        .input-container {
            display: grid;
            grid-template-columns: auto 30px 20px;
            padding: 0 10px;
            height: 45px;

            label {
                margin-bottom: 0;
                margin-right: auto;
                align-self: center;
                cursor: pointer;

                &.icon {
                    color: $passive;

                    &:hover {
                        color: $active;
                    }
                }
            }

            input {
                background-color: transparent;
                border: none;
                align-self: center;
                color: $active;
                text-align: center;

                &::-webkit-input-placeholder {
                    color: $passive;
                }

                &:focus ~ label {
                    color: $active;
                }
            }

            @include input-number-no-arrows();

            @media screen {
                column-gap: 10px;
                padding: 0 10px;
                border: solid 1px $passive;
            }
            @media print {
                column-gap: 5px;
                grid-template-columns: auto 30px;
                border-radius: 0;

                label,
                input {
                    color: black;
                }

                label:last-child {
                    display: none;
                }
            }
        }

        .total {
            height: 100%;

            @media screen {
                border-radius: 4px;
            }
            @media print {
                border: solid 1px $report-color;
            }
        }

        .fields {
            display: grid;
            grid-template-rows: repeat(2, 1fr);

            @media screen {
                row-gap: 10px;
            }
            @media print {
                border: solid 1px $report-color;
            }

            .insemination-type {
                display: grid;
                grid-template-columns: repeat(2, 1fr);

                @media screen {
                    border-radius: 4px;

                    :first-child {
                        border-top-left-radius: 4px;
                        border-bottom-left-radius: 4px;
                    }
                    :last-child {
                        border-top-right-radius: 4px;
                        border-bottom-right-radius: 4px;
                    }
                }

                @media print {
                    &:first-child {
                        border-bottom: solid 1px $report-color;
                    }

                    .input-container:first-child {
                        border-right: solid 1px $report-color;
                    }
                }
            }
        }
    }
}
</style>
